import { createListenerMiddleware, isAnyOf, type ListenerEffectAPI } from '@reduxjs/toolkit'
import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import { AppDispatch, RootState } from '@/store/store'
import { kConversationListCacheDuration, kConversationRefreshCacheDuration, kNewConversationTitle } from '@/chat-common/conversation_list/constants'
import { getAgentV2ConversationDetail } from '@/agent-v2/conversations/fetch/get-agent-v2-conversation-detail'
import { getAgentV2ConversationList } from '@/agent-v2/conversations/fetch/get-agent-v2-conversation-list'
import { patchGenerateAgentV2ConversationTitle } from '@/agent-v2/conversations/fetch/patch-generate-agent-v2-conversation-title'
import { postGenerateAgentV2ConversationFollowUpQuestions } from '@/agent-v2/conversations/fetch/post-generate-agent-v2-conversation-follow-up-questions'
import { putAgentV2TurnRequest } from '@/agent-v2/conversations/fetch/put-agent-v2-turn-request'
import { AgentV2ReferencesActions } from '@/agent-v2/references/store/slice'
import { reportDroppedAgentV2References, sanitizeAgentV2References } from '@/agent-v2/references/agent-v2-reference-sanitizer'
import { hydrateAgentV2ConversationTemplateState } from '@/agent-v2/artifact-templates/utils/hydrate-conversation-template-state'
import { AgentV2ConversationsActions, AgentV2DetailLoadError, AgentV2RunPhase, getLatestTurn } from './slice'
import {
  isAgentV2ConversationOwnedByHost,
  selectAgentV2ConversationChatFileIds,
  selectAgentV2ConversationChatWritingSampleFileId,
  selectAgentV2ConversationFromId,
  selectAgentV2RunPhase,
} from './selectors'
import { MatterArtifactSelectionActions } from '@/agent-v2/artifact-selection/store/slice'
import { ApiError } from '@/util/errors/api-error'
import { MatterArtifactVersionStatus } from '@/matter-artifacts/schemas'
import { MatterArtifactsActions } from '@/matter-artifacts/store/slice'
import { AgentV2RunFailureCode, AgentV2ServerEventType, type AgentV2ConversationDetailResponse, type AgentV2PersistedTurn } from '@/agent-v2/store/schemas'
import { AgentV2TransportActions, AgentV2TransportPhase } from '@/agent-v2/transport/store/slice'
import { AgentV2TranscriptActions } from '@/agent-v2/store/slice'
import { AgentConversationFormActions } from '@/agent-v2/chat-form/store/slice'
import { fetchAndStoreUserFiles } from '@/user_files/files/utils/fetch-and-store-user-files'
import type { UserFileRead } from '@/uppy-file-upload/schemas'
import { readAttachedSkillNames } from '@/user-skills/utils/attached-skill-storage'
import { notifySettledDocxExports } from '@/agent-v2/docx-export/store/notify-settled-exports'
import { clearOpenDocxExportPolls, handleOpenDocxExportPollError, scheduleOpenDocxExportPoll } from '@/agent-v2/docx-export/store/poll-open-exports'
import { mergeConversationDetail } from '@/agent-v2/conversations/utils/merge-conversation-detail'

export const agentV2ConversationsListenerMiddleware = createListenerMiddleware()
const startListener = agentV2ConversationsListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()
const kRunRecoveryPollIntervalMs = 3000
const kAdmissionFailureMessage = 'Unable to safely resume your last request. Please try again.'
const kTurnAlreadyActiveMessage = 'This conversation already has a response in progress. Wait for it to finish, then try again.'
const kTurnAlreadyActiveErrorCode = 'turn_already_active'

startListener({
  matcher: isAnyOf(AgentV2TranscriptActions.serverEventReceived, AgentV2ConversationsActions.setConversationDetail),
  effect: (action, listenerApi) => {
    if (!AgentV2TranscriptActions.serverEventReceived.match(action) && !AgentV2ConversationsActions.setConversationDetail.match(action)) return
    const isLiveEvent = AgentV2TranscriptActions.serverEventReceived.match(action)
    const conversationId = isLiveEvent ? action.payload.conversationId : action.payload.conversation.id
    const previousLifecycle = listenerApi.getOriginalState().agentV2ConversationsState.runLifecycleByConversationId[conversationId]
    // Initial history loads and repeat terminal snapshots are not new responses.
    if (!previousLifecycle || previousLifecycle.phase === AgentV2RunPhase.IDLE) return

    if (isLiveEvent) {
      if (action.payload.event.type !== AgentV2ServerEventType.RUN_COMPLETED) return
    } else {
      const turn = action.payload.turns.find((candidate) =>
        previousLifecycle.turnId ? candidate.id === previousLifecycle.turnId : candidate.client_request_id === previousLifecycle.clientRequestId
      )
      if (!turn?.ended_at || turn.did_fail || turn.did_cancel) return
    }

    if (listenerApi.getState().chatUxState.activeConversationId !== conversationId) {
      listenerApi.dispatch(AgentV2ConversationsActions.markResponseUnread({ conversationId }))
    }
  },
})

startListener({
  actionCreator: AgentV2ConversationsActions.refreshConversationList,
  effect: async (action, listenerApi) => {
    const { matterId, forceRefresh } = action.payload
    const state = listenerApi.getState().agentV2ConversationsState
    if (action.payload.sidebar) {
      const list = state.sidebarListsByMatterId[matterId]
      if (!forceRefresh && (list?.loading || (list?.lastRefresh != null && Date.now() - list.lastRefresh <= kConversationListCacheDuration))) return
      const requestId = nanoid()
      listenerApi.dispatch(AgentV2ConversationsActions.beginSidebarRefresh({ matterId, requestId }))
      try {
        const response = await getAgentV2ConversationList(matterId, state.hostSurface, { limit: 20, createdByMe: true, hasActivity: true })
        if (listenerApi.getState().agentV2ConversationsState.sidebarListsByMatterId[matterId]?.requestId !== requestId) return
        listenerApi.dispatch(AgentV2ConversationsActions.finishSidebarRefresh({ matterId, conversations: response.conversations }))
      } catch {
        if (listenerApi.getState().agentV2ConversationsState.sidebarListsByMatterId[matterId]?.requestId !== requestId) return
        listenerApi.dispatch(AgentV2ConversationsActions.finishSidebarRefresh({ matterId }))
      }
      return
    }
    if (forceRefresh && state.sidebarListsByMatterId[matterId]) {
      listenerApi.dispatch(AgentV2ConversationsActions.refreshConversationList({ matterId, forceRefresh: true, sidebar: true }))
    }
    // Once Chats requests full history, keep this shared cache complete until auth reset.
    // Mutation/overview refreshes also become unlimited; a later 50-row response
    // would otherwise truncate the Chats page. The sidebar stays separately capped.
    const all = action.payload.all || state.allRequestedByMatterId[matterId] || false
    const upgrading = all && !state.allRequestedByMatterId[matterId]
    const needsFullList = all && !state.fullListLoadedByMatterId[matterId]
    const isLoading = state.loadingByMatterId[matterId] ?? false
    const lastRefresh = state.lastRefreshByMatterId[matterId] ?? null
    const cacheExpired = lastRefresh === null || Date.now() - lastRefresh > kConversationListCacheDuration

    // Forced refreshes supersede in-flight work. The request id guard below prevents an older
    // response from overwriting the newer result when requests finish out of order.
    if (!forceRefresh && ((!cacheExpired && !needsFullList) || (isLoading && !upgrading))) return

    const requestId = nanoid()
    listenerApi.dispatch(AgentV2ConversationsActions.beginListRefresh({ matterId, requestId, all }))

    try {
      const response = all
        ? await getAgentV2ConversationList(matterId, state.hostSurface, { limit: null })
        : await getAgentV2ConversationList(matterId, state.hostSurface)

      if (listenerApi.getState().agentV2ConversationsState.requestIdByMatterId[matterId] !== requestId) return

      listenerApi.dispatch(AgentV2ConversationsActions.replaceConversationList({ matterId, conversations: response.conversations }))
      listenerApi.dispatch(AgentV2ConversationsActions.setFullListLoaded({ matterId, loaded: all }))
      // Only a successful load completes the initial load or advances the cache timestamp — a failed
      // load must stay retryable immediately and must never look like "loaded empty" (ENG-7069)
      listenerApi.dispatch(AgentV2ConversationsActions.setInitialLoadComplete({ matterId, complete: true }))
      listenerApi.dispatch(AgentV2ConversationsActions.setListLoading({ matterId, loading: false }))
      listenerApi.dispatch(AgentV2ConversationsActions.setListLastRefresh({ matterId, lastRefresh: Date.now() }))
    } catch {
      if (listenerApi.getState().agentV2ConversationsState.requestIdByMatterId[matterId] !== requestId) return

      listenerApi.dispatch(AgentV2ConversationsActions.setListError({ matterId, error: true }))
      listenerApi.dispatch(AgentV2ConversationsActions.setListLoading({ matterId, loading: false }))
    }
  },
})

startListener({
  actionCreator: AgentV2ConversationsActions.fetchConversationDetail,
  effect: async (action, listenerApi) => {
    const { conversationId, forceRefresh, treatNotFoundAsDeleted } = action.payload
    const state = listenerApi.getState()
    if (state.agentV2ConversationsState.deletionStatusByConversationId[conversationId]) return
    const conversation = selectAgentV2ConversationFromId(state, { conversationId })
    const isLoading = state.agentV2ConversationsState.detailLoadingByConversationId[conversationId] ?? false
    const lastRefresh = state.agentV2ConversationsState.detailLastRefreshByConversationId[conversationId] ?? conversation?.lastRefresh ?? null
    const cacheExpired = lastRefresh === null || Date.now() - lastRefresh > kConversationRefreshCacheDuration

    // Forced refreshes supersede in-flight work. The request id guard prevents an older
    // response from restoring data after a newer request or an auth-state reset.
    if (!forceRefresh && (!cacheExpired || isLoading)) return

    const requestId = nanoid()
    listenerApi.dispatch(AgentV2ConversationsActions.beginDetailRefresh({ conversationId, requestId }))
    const abortController = new AbortController()
    const cancellationTask = listenerApi.fork(async () => {
      await listenerApi.condition(
        (candidateAction) =>
          (AgentV2ConversationsActions.conversationDeletionStarted.match(candidateAction) && candidateAction.payload.conversationId === conversationId) ||
          AgentV2ConversationsActions.nullifyData.match(candidateAction)
      )
      abortController.abort()
    })

    try {
      let detail = treatNotFoundAsDeleted
        ? await getAgentV2ConversationDetail(conversationId, abortController.signal, { expectedNotFound: true, paginated: true })
        : await getAgentV2ConversationDetail(conversationId, abortController.signal, { paginated: true })

      // A chat continued elsewhere can advance beyond our cached page. Fill that gap before
      // merging, otherwise the retained older cursor would skip the intervening turns forever.
      const cachedTurns = new Set(state.agentV2ConversationsState.details[conversationId]?.turns.map((turn) => turn.id))
      let cursor = detail.next_cursor
      while (cachedTurns.size && cursor && !detail.turns.some((turn) => cachedTurns.has(turn.id))) {
        if (listenerApi.getState().agentV2ConversationsState.detailRequestIdByConversationId[conversationId] !== requestId) return
        const older = await getAgentV2ConversationDetail(conversationId, abortController.signal, { cursor })
        cursor = older.next_cursor
        detail = mergeConversationDetail(older, detail)
      }

      if (listenerApi.getState().agentV2ConversationsState.detailRequestIdByConversationId[conversationId] !== requestId) return

      // Draft links from add-ins open read-only in the browser. Keep their citation targets
      // available without hydrating the foreign conversation's transcript or composer.
      // There is no chat pager on that surface, so retain its complete reference hydration.
      if (
        detail.next_cursor &&
        !isAgentV2ConversationOwnedByHost(detail.conversation.origin_surface, listenerApi.getState().agentV2ConversationsState.hostSurface)
      ) {
        detail = await getAgentV2ConversationDetail(conversationId, abortController.signal)
        if (listenerApi.getState().agentV2ConversationsState.detailRequestIdByConversationId[conversationId] !== requestId) return
      }
      const { accepted, dropped } = sanitizeAgentV2References(detail.conversation.id, detail.references)
      reportDroppedAgentV2References(detail.conversation.id, dropped)
      listenerApi.dispatch(AgentV2ReferencesActions.upsertReferences({ conversationId: detail.conversation.id, references: accepted }))

      // Conversations are host-only. A direct link to another host's conversation keeps the
      // summary for the route's host-only state but never hydrates chat or composer state.
      // An absent surface (older backend response) stays browser-renderable.
      if (!isAgentV2ConversationOwnedByHost(detail.conversation.origin_surface, listenerApi.getState().agentV2ConversationsState.hostSurface)) {
        listenerApi.dispatch(AgentV2ConversationsActions.upsertConversationFromSummary(detail.conversation))
        listenerApi.dispatch(AgentV2ConversationsActions.setConversationError({ conversationId, loadError: AgentV2DetailLoadError.ADD_IN_ONLY }))
        listenerApi.dispatch(AgentV2ConversationsActions.setConversationLoading({ conversationId, isLoading: false }))
        return
      }

      // Load this page's file records before rendering its chips. Only the initial load
      // seeds Chat files; subsequent pages and refreshes preserve the current selection.
      const hasChatFiles = () => listenerApi.getState().agentV2ConversationsState.chatFilesByConversationId[conversationId] !== undefined
      const shouldSeedChatFiles = lastRefresh === null && !hasChatFiles()
      const loadedTurnIds = new Set(listenerApi.getState().agentV2ConversationsState.details[conversationId]?.turns.map((turn) => turn.id))
      const turnsToLoad = shouldSeedChatFiles ? detail.turns : detail.turns.filter((turn) => !loadedTurnIds.has(turn.id))
      const turnFileRecords = await fetchTurnFileRecords(turnsToLoad, listenerApi)
      if (listenerApi.getState().agentV2ConversationsState.detailRequestIdByConversationId[conversationId] !== requestId) return

      listenerApi.dispatch(AgentV2ConversationsActions.setConversationDetail(detail))
      // A selected-passage edit keeps its selection until the user's turn has ended and the run is
      // idle. Rereads also arrive mid-submit (the export poll runs every 3 s), and an export's
      // notice turn ends as it is written, so neither may end the edit's selection.
      if (selectAgentV2RunPhase(listenerApi.getState(), { conversationId }) === AgentV2RunPhase.IDLE && getLatestTurn(detail)?.ended_at) {
        listenerApi.dispatch(MatterArtifactSelectionActions.clearMatterArtifactSelection({ conversationId }))
      }
      // Live completion and REST recovery both hydrate detail. New activity can
      // move an older chat into the sidebar's latest 20, so reconcile membership.
      if (
        detail.conversation.has_activity &&
        detail.conversation.updated_at !== conversation?.updated_at &&
        listenerApi.getState().agentV2ConversationsState.sidebarListsByMatterId[detail.conversation.matter_id]
      ) {
        listenerApi.dispatch(
          AgentV2ConversationsActions.refreshConversationList({ matterId: detail.conversation.matter_id, forceRefresh: true, sidebar: true })
        )
      }
      // The detail cache accumulates history, but citation resolution uses the
      // conversation-scoped reference bucket populated above.
      // After the first load, each sent turn keeps the chat's files up to date
      if (shouldSeedChatFiles && !hasChatFiles()) restoreAttachedFilesFromLatestTurn(detail, turnFileRecords, listenerApi)
      // Seed the web-search toggle only on the first load. Reopening or background refreshes must preserve unsent changes.
      if (lastRefresh === null) {
        const latestTurn = getLatestTurn(detail)
        if (latestTurn) {
          listenerApi.dispatch(
            AgentConversationFormActions.setWebSearchEnabled({
              conversationId,
              enabled: latestTurn.search_defaults.web_search_enabled ?? true,
            })
          )
        }
      }
      // Only a successful load advances the cache timestamp — a failed load must stay retryable immediately (ENG-7070)
      listenerApi.dispatch(AgentV2ConversationsActions.setConversationLastRefresh({ conversationId, lastRefresh: Date.now() }))
    } catch (error) {
      if (abortController.signal.aborted) return
      if (listenerApi.getState().agentV2ConversationsState.detailRequestIdByConversationId[conversationId] !== requestId) return

      const notFound = error instanceof ApiError && error.status === 404
      if (notFound && treatNotFoundAsDeleted) {
        listenerApi.dispatch(AgentV2ConversationsActions.deleteConversation({ conversationId }))
        return
      }

      // Only a server-confirmed 404 is terminal; 500s, network failures, and schema mismatches are retryable (ENG-7070)
      const loadError = notFound ? AgentV2DetailLoadError.NOT_FOUND : AgentV2DetailLoadError.RETRYABLE
      listenerApi.dispatch(AgentV2ConversationsActions.setConversationError({ conversationId, loadError }))
      listenerApi.dispatch(AgentV2ConversationsActions.setConversationLoading({ conversationId, isLoading: false }))
    } finally {
      cancellationTask.cancel()
    }
  },
})

startListener({
  actionCreator: AgentV2ConversationsActions.fetchOlderConversationHistory,
  effect: async (action, listenerApi) => {
    const { conversationId } = action.payload
    const state = listenerApi.getState().agentV2ConversationsState
    const history = state.historyByConversationId[conversationId]
    if (state.deletionStatusByConversationId[conversationId] || !history?.nextCursor || history.loading) return
    const requestId = nanoid()
    listenerApi.dispatch(AgentV2ConversationsActions.beginOlderHistoryLoad({ conversationId, requestId }))
    const abortController = new AbortController()
    const cancellationTask = listenerApi.fork(async () => {
      await listenerApi.condition(
        (candidateAction) =>
          (AgentV2ConversationsActions.conversationDeletionStarted.match(candidateAction) && candidateAction.payload.conversationId === conversationId) ||
          AgentV2ConversationsActions.nullifyData.match(candidateAction)
      )
      abortController.abort()
    })
    try {
      const detail = await getAgentV2ConversationDetail(conversationId, abortController.signal, { cursor: history.nextCursor })
      if (listenerApi.getState().agentV2ConversationsState.historyByConversationId[conversationId]?.requestId !== requestId) return
      await fetchTurnFileRecords(detail.turns, listenerApi)
      if (listenerApi.getState().agentV2ConversationsState.historyByConversationId[conversationId]?.requestId !== requestId) return
      const { accepted, dropped } = sanitizeAgentV2References(conversationId, detail.references)
      reportDroppedAgentV2References(conversationId, dropped)
      listenerApi.dispatch(AgentV2ReferencesActions.upsertReferences({ conversationId, references: accepted }))
      listenerApi.dispatch(AgentV2ConversationsActions.olderConversationHistoryReceived(detail))
    } catch {
      if (listenerApi.getState().agentV2ConversationsState.historyByConversationId[conversationId]?.requestId !== requestId) return
      listenerApi.dispatch(AgentV2ConversationsActions.olderHistoryLoadFailed({ conversationId }))
    } finally {
      cancellationTask.cancel()
    }
  },
})

startListener({
  matcher: isAnyOf(AgentV2ConversationsActions.setConversationDetail, AgentV2ConversationsActions.olderConversationHistoryReceived),
  effect: (action, listenerApi) => {
    if (!AgentV2ConversationsActions.setConversationDetail.match(action) && !AgentV2ConversationsActions.olderConversationHistoryReceived.match(action)) return
    const conversationId = action.payload.conversation.id
    const userId = getAuth().currentUser?.uid
    if (userId) {
      listenerApi.dispatch(
        AgentV2TranscriptActions.restoreAttachedSkillNames({ conversationId, namesByRequestId: readAttachedSkillNames(userId, conversationId) })
      )
    }
    ingestDurableMatterArtifactVersions(action.payload, listenerApi)
    if (AgentV2ConversationsActions.setConversationDetail.match(action)) {
      notifySettledDocxExports(listenerApi.getOriginalState().agentV2ConversationsState.details[conversationId], action.payload, listenerApi)
    }
    scheduleOpenDocxExportPoll(conversationId, listenerApi)
    // Hydrate persisted template state alongside the versions it belongs to; the util reconciles
    // this conversation's artifact scope (removing stale entries the server no longer has) and
    // resolves the referenced template files so the toolbar chip can show their names. The scope
    // unions per-turn versions with the top-level history because the top-level list is
    // creator-scoped on the server: for a collaborator it can be empty while the backend hydrated
    // template state from turn-linked versions, and a narrower scope would reconcile-delete those.
    const templateScopeVersions = [...action.payload.turns.flatMap((turn) => turn.artifact_versions), ...action.payload.artifact_versions]
    hydrateAgentV2ConversationTemplateState({
      selections: action.payload.template_selections,
      appliedTemplates: action.payload.applied_templates,
      matterArtifactIds: [...new Set(templateScopeVersions.map((artifactVersion) => artifactVersion.matter_artifact_id))],
      matterArtifactVersionIds: [...new Set(templateScopeVersions.map((artifactVersion) => artifactVersion.id))],
      dispatch: listenerApi.dispatch,
    })
    if (AgentV2ConversationsActions.olderConversationHistoryReceived.match(action)) return
    requestRecoveredTurnFollowUps(action.payload, listenerApi)
    settleDetachedTransportAfterDurableTerminal(action.payload, listenerApi)

    const latestTurn = getLatestTurn(action.payload)
    if (!latestTurn || latestTurn.ended_at !== null) return

    const lifecycle = listenerApi.getState().agentV2ConversationsState.runLifecycleByConversationId[conversationId]
    const isUntrackedOpenTurn = !lifecycle || (lifecycle.phase === AgentV2RunPhase.IDLE && lifecycle.turnId !== latestTurn.id)
    if (isUntrackedOpenTurn) listenerApi.dispatch(AgentV2ConversationsActions.beginRunRecovery({ conversationId }))
  },
})

function ingestDurableMatterArtifactVersions(detail: AgentV2ConversationDetailResponse, listenerApi: ListenerEffectAPI<RootState, AppDispatch>) {
  const processedVersionIds = new Set<string>()
  for (const matterArtifactVersions of [...detail.turns.map((turn) => turn.artifact_versions), detail.artifact_versions]) {
    for (const matterArtifactVersion of matterArtifactVersions) {
      if (processedVersionIds.has(matterArtifactVersion.id)) continue
      processedVersionIds.add(matterArtifactVersion.id)

      const previousVersion = listenerApi.getState().matterArtifactsState.versionsById[matterArtifactVersion.id]
      listenerApi.dispatch(MatterArtifactsActions.upsertMatterArtifactVersion(matterArtifactVersion))
      if (matterArtifactVersion.status === MatterArtifactVersionStatus.GENERATING && previousVersion?.status !== MatterArtifactVersionStatus.GENERATING) {
        listenerApi.dispatch(MatterArtifactsActions.startMatterArtifactVersionStatusPoll({ matterArtifactVersionId: matterArtifactVersion.id }))
      }
    }
  }
}

/**
 * Loads file records for the turns in this history page. Resolves null when the fetch fails, so a file
 * lookup never blocks the conversation from opening.
 */
async function fetchTurnFileRecords(
  turns: AgentV2PersistedTurn[],
  listenerApi: ListenerEffectAPI<RootState, AppDispatch>
): Promise<Record<string, UserFileRead> | null> {
  const fileIds = turns.flatMap((turn) => {
    const writingSampleFileId = turn.search_defaults.writing_sample_file_id
    return writingSampleFileId ? [...turn.search_defaults.attached_file_ids, writingSampleFileId] : turn.search_defaults.attached_file_ids
  })
  if (fileIds.length === 0) return {}

  try {
    return await fetchAndStoreUserFiles(fileIds, listenerApi.dispatch)
  } catch {
    return null
  }
}

/** Seeds the chat's files and writing sample from the latest turn. */
function restoreAttachedFilesFromLatestTurn(
  detail: AgentV2ConversationDetailResponse,
  turnFileRecords: Record<string, UserFileRead> | null,
  listenerApi: ListenerEffectAPI<RootState, AppDispatch>
) {
  const latestTurn = getLatestTurn(detail)
  if (!latestTurn) return

  // A file deleted or moved to another matter since the turn is dropped, so the next turn doesn't send it. A failed fetch
  // keeps every file.
  const isAvailable = (fileId: string) => !turnFileRecords || turnFileRecords[fileId]?.matter_id === detail.conversation.matter_id
  const writingSampleFileId = latestTurn.search_defaults.writing_sample_file_id ?? null
  listenerApi.dispatch(
    AgentV2ConversationsActions.setChatFiles({
      conversationId: detail.conversation.id,
      fileIds: latestTurn.search_defaults.attached_file_ids.filter(isAvailable),
      writingSampleFileId: writingSampleFileId && isAvailable(writingSampleFileId) ? writingSampleFileId : null,
    })
  )
}

function settleDetachedTransportAfterDurableTerminal(detail: AgentV2ConversationDetailResponse, listenerApi: ListenerEffectAPI<RootState, AppDispatch>) {
  const conversationId = detail.conversation.id
  const lifecycle = listenerApi.getState().agentV2ConversationsState.runLifecycleByConversationId[conversationId]
  const transport = listenerApi.getState().agentV2TransportState.byConversationId[conversationId]
  if (lifecycle?.phase !== AgentV2RunPhase.IDLE || transport?.phase !== AgentV2TransportPhase.DETACHED || !transport.clientRequestId) return
  listenerApi.dispatch(AgentV2TransportActions.transportSettled({ conversationId, clientRequestId: transport.clientRequestId }))
}

function requestRecoveredTurnFollowUps(detail: AgentV2ConversationDetailResponse, listenerApi: ListenerEffectAPI<RootState, AppDispatch>) {
  const conversationId = detail.conversation.id
  const originalLifecycle = listenerApi.getOriginalState().agentV2ConversationsState.runLifecycleByConversationId[conversationId]
  if (originalLifecycle?.phase !== AgentV2RunPhase.RECOVERING) return

  if (detail.conversation.created_by_user_id !== getAuth().currentUser?.uid) return

  const currentLifecycle = listenerApi.getState().agentV2ConversationsState.runLifecycleByConversationId[conversationId]
  if (currentLifecycle?.phase !== AgentV2RunPhase.IDLE) return

  const resolvedTurn = originalLifecycle.turnId
    ? detail.turns.find((turn) => turn.id === originalLifecycle.turnId)
    : originalLifecycle.clientRequestId
    ? detail.turns.find((turn) => turn.client_request_id === originalLifecycle.clientRequestId)
    : undefined
  if (!resolvedTurn?.ended_at || resolvedTurn.did_fail || resolvedTurn.did_cancel) return

  const assistantMessageId = `${resolvedTurn.id}:assistant`
  if (listenerApi.getState().agentV2ConversationsState.followUpQuestionsByAssistantMessageId[assistantMessageId]) return
  listenerApi.dispatch(AgentV2ConversationsActions.generateConversationFollowUpQuestions({ conversationId, assistantMessageId }))
}

startListener({
  actionCreator: AgentV2ConversationsActions.beginRunRecovery,
  effect: async (action, listenerApi) => {
    const { conversationId } = action.payload
    const originalGeneration = listenerApi.getOriginalState().agentV2ConversationsState.runLifecycleByConversationId[conversationId]?.recoveryGeneration ?? 0
    const recoveryGeneration = listenerApi.getState().agentV2ConversationsState.runLifecycleByConversationId[conversationId]?.recoveryGeneration ?? 0
    // A duplicate begin action is a reducer no-op and must not create a second poll loop.
    if (recoveryGeneration === originalGeneration) return

    const initialLifecycle = listenerApi.getState().agentV2ConversationsState.runLifecycleByConversationId[conversationId]
    if (initialLifecycle?.pendingRequest) {
      listenerApi.dispatch(AgentV2ConversationsActions.requestTurnAdmissionRecovery({ conversationId }))
    }

    while (true) {
      await listenerApi.delay(kRunRecoveryPollIntervalMs)

      const state = listenerApi.getState().agentV2ConversationsState
      const lifecycle = state.runLifecycleByConversationId[conversationId]
      if (lifecycle?.phase !== AgentV2RunPhase.RECOVERING || lifecycle.recoveryGeneration !== recoveryGeneration) return
      if (state.deletionStatusByConversationId[conversationId]) continue
      if (lifecycle.pendingRequest) {
        if (!lifecycle.admissionFallbackInFlight) {
          listenerApi.dispatch(AgentV2ConversationsActions.requestTurnAdmissionRecovery({ conversationId }))
        }
        continue
      }
      // A forced refresh supersedes in-flight work. Skipping this tick preserves
      // single-flight recovery and prevents every slow response from becoming stale.
      if (state.detailLoadingByConversationId[conversationId]) continue

      listenerApi.dispatch(AgentV2ConversationsActions.fetchConversationDetail({ conversationId, forceRefresh: true }))
    }
  },
})

startListener({
  actionCreator: AgentV2ConversationsActions.conversationDeletionFailed,
  effect: (action, listenerApi) => {
    const lifecycle = listenerApi.getState().agentV2ConversationsState.runLifecycleByConversationId[action.payload.conversationId]
    if (!lifecycle || lifecycle.phase === AgentV2RunPhase.IDLE || lifecycle.phase === AgentV2RunPhase.RECOVERING) return
    listenerApi.dispatch(AgentV2ConversationsActions.beginRunRecovery({ conversationId: action.payload.conversationId }))
  },
})

startListener({
  actionCreator: AgentV2ConversationsActions.requestTurnAdmissionRecovery,
  effect: async (action, listenerApi) => {
    const { conversationId } = action.payload
    const originalLifecycle = listenerApi.getOriginalState().agentV2ConversationsState.runLifecycleByConversationId[conversationId]
    const lifecycle = listenerApi.getState().agentV2ConversationsState.runLifecycleByConversationId[conversationId]
    if (
      originalLifecycle?.admissionFallbackInFlight ||
      lifecycle?.phase !== AgentV2RunPhase.RECOVERING ||
      !lifecycle.admissionFallbackInFlight ||
      !lifecycle.clientRequestId ||
      !lifecycle.pendingRequest
    ) {
      return
    }

    const { clientRequestId, pendingRequest } = lifecycle
    try {
      const response = await putAgentV2TurnRequest({ clientRequestId, conversationId, request: pendingRequest })
      const currentLifecycle = listenerApi.getState().agentV2ConversationsState.runLifecycleByConversationId[conversationId]
      if (currentLifecycle?.phase !== AgentV2RunPhase.RECOVERING || currentLifecycle.clientRequestId !== clientRequestId) return

      listenerApi.dispatch(
        AgentV2ConversationsActions.turnAdmissionRecovered({
          clientRequestId,
          conversationId,
          turnId: response.turn_id,
        })
      )
      const conversation = selectAgentV2ConversationFromId(listenerApi.getState(), { conversationId })
      if (conversation?.title === kNewConversationTitle) {
        listenerApi.dispatch(AgentV2ConversationsActions.generateConversationTitle({ conversationId }))
      }
      listenerApi.dispatch(AgentV2ConversationsActions.fetchConversationDetail({ conversationId, forceRefresh: true }))
    } catch (error) {
      const currentLifecycle = listenerApi.getState().agentV2ConversationsState.runLifecycleByConversationId[conversationId]
      if (currentLifecycle?.phase !== AgentV2RunPhase.RECOVERING || currentLifecycle.clientRequestId !== clientRequestId) return

      listenerApi.dispatch(AgentV2ConversationsActions.turnAdmissionRecoveryFailed({ clientRequestId, conversationId }))
      if (!isDefinitiveAdmissionFailure(error)) return

      const message = isTurnAlreadyActiveRejection(error) ? kTurnAlreadyActiveMessage : kAdmissionFailureMessage
      listenerApi.dispatch(AgentV2TranscriptActions.turnFailedBeforeAdmission({ conversationId, message }))
      listenerApi.dispatch(AgentV2ConversationsActions.runDidNotStart({ conversationId }))
      const transport = listenerApi.getState().agentV2TransportState.byConversationId[conversationId]
      if (transport?.phase === AgentV2TransportPhase.DETACHED && transport.clientRequestId === clientRequestId) {
        listenerApi.dispatch(AgentV2TransportActions.transportSettled({ clientRequestId, conversationId }))
      }
    }
  },
})

function isTurnAlreadyActiveRejection(error: ApiError): boolean {
  if (error.status !== 409 || typeof error.detail !== 'object' || error.detail === null) return false
  return (error.detail as { code?: unknown }).code === kTurnAlreadyActiveErrorCode
}

function isDefinitiveAdmissionFailure(error: unknown): error is ApiError {
  if (!(error instanceof ApiError)) return false
  return error.status >= 400 && error.status < 500 && error.status !== 408 && error.status !== 429
}

startListener({
  actionCreator: AgentV2ConversationsActions.generateConversationTitle,
  effect: async (action, listenerApi) => {
    const { conversationId } = action.payload
    const lifecycleGeneration = listenerApi.getState().agentV2ConversationsState.lifecycleGeneration
    const conversation = selectAgentV2ConversationFromId(listenerApi.getState(), { conversationId })

    try {
      const updatedSummary = await patchGenerateAgentV2ConversationTitle(conversationId)
      if (listenerApi.getState().agentV2ConversationsState.lifecycleGeneration !== lifecycleGeneration) return

      listenerApi.dispatch(AgentV2ConversationsActions.upsertConversationFromSummary(updatedSummary))

      if (conversation?.matter_id) {
        listenerApi.dispatch(AgentV2ConversationsActions.refreshConversationList({ matterId: conversation.matter_id, forceRefresh: true }))
      }
      listenerApi.dispatch(AgentV2ConversationsActions.fetchConversationDetail({ conversationId, forceRefresh: true }))
    } catch (error) {
      if (listenerApi.getState().agentV2ConversationsState.lifecycleGeneration !== lifecycleGeneration) return
      console.error('Failed to generate Agent v2 conversation title', error)
      listenerApi.dispatch(AgentV2ConversationsActions.conversationTitleGenerationFailed({ conversationId }))
    }
  },
})

// A turn fails as a whole when one of its files is gone. Re-checking the chat's files marks the missing ones deleted,
// so the next turn leaves them out instead of failing the same way.
startListener({
  actionCreator: AgentV2TranscriptActions.serverEventReceived,
  effect: async (action, listenerApi) => {
    const { conversationId, event } = action.payload
    if (event.type !== AgentV2ServerEventType.RUN_FAILED || event.payload.code !== AgentV2RunFailureCode.ATTACHED_FILES_UNAVAILABLE) return

    const state = listenerApi.getState()
    const writingSampleFileId = selectAgentV2ConversationChatWritingSampleFileId(state, { conversationId })
    const chatFileIds = selectAgentV2ConversationChatFileIds(state, { conversationId })
    try {
      await fetchAndStoreUserFiles(writingSampleFileId ? [...chatFileIds, writingSampleFileId] : chatFileIds, listenerApi.dispatch)
    } catch {
      // A failed re-check leaves the chat's files as they were, and the next send can try again
    }
  },
})

startListener({
  actionCreator: AgentV2ConversationsActions.generateConversationFollowUpQuestions,
  effect: async (action, listenerApi) => {
    const { assistantMessageId, conversationId } = action.payload
    if (listenerApi.getOriginalState().agentV2ConversationsState.followUpQuestionsByAssistantMessageId[assistantMessageId]) return
    const lifecycleGeneration = listenerApi.getState().agentV2ConversationsState.lifecycleGeneration

    try {
      const response = await postGenerateAgentV2ConversationFollowUpQuestions(conversationId)
      if (listenerApi.getState().agentV2ConversationsState.lifecycleGeneration !== lifecycleGeneration) return

      listenerApi.dispatch(
        AgentV2ConversationsActions.setFollowUpQuestionsState({
          assistantMessageId,
          conversationId,
          loading: false,
          isError: false,
          questions: response.questions,
        })
      )
    } catch (error) {
      if (listenerApi.getState().agentV2ConversationsState.lifecycleGeneration !== lifecycleGeneration) return
      console.error('Failed to generate Agent v2 follow up questions', error)
      listenerApi.dispatch(
        AgentV2ConversationsActions.setFollowUpQuestionsState({
          assistantMessageId,
          conversationId,
          loading: false,
          isError: true,
        })
      )
    }
  },
})

startListener({
  actionCreator: AgentV2ConversationsActions.docxExportAdmitted,
  effect: (action, listenerApi) => scheduleOpenDocxExportPoll(action.payload.conversationId, listenerApi),
})

startListener({
  actionCreator: AgentV2ConversationsActions.setConversationError,
  effect: (action, listenerApi) => {
    // A transient failure keeps polling, so it doesn't strand an open export; a chat that is gone stops.
    handleOpenDocxExportPollError(action.payload.conversationId, action.payload.loadError, listenerApi)
  },
})

startListener({
  actionCreator: AgentV2ConversationsActions.nullifyData,
  effect: () => clearOpenDocxExportPolls(),
})
